// Pending approvals: what the "approvals are waiting" notice, the sidebar
// count and the Audit Logs approvals view read.
//
// Source, in order:
//  1. GET /api/automation/approvals/pending -> { count, approvals: [row] },
//     every pending approval for the workspace whatever its age. This route
//     is specified for the backend but may not be deployed yet.
//  2. While that route answers 404, the newest page of the audit log
//     (GET /api/automation/logs?page=1&pageSize=500), the same rows the
//     Audit Logs page shows first. An approval older than that page is then
//     not counted, which `complete: false` says.
import {
  dedupeVisibleApprovalLogs,
  formatPeople,
  isActionableApproval,
  logMeta,
} from "./auditLogs";

const API =
  process.env.REACT_APP_API_BASE ||
  process.env.REACT_APP_API_URL ||
  "https://api.bossdrop.ai";

export const PENDING_APPROVALS_PATH = "/api/automation/approvals/pending";
const RECENT_LOGS_PATH = "/api/automation/logs?page=1&pageSize=500";

// Session-scoped: dismissing the notice hides it until this browser session
// ends or the person signs in again (resetApprovalsNoticeDismissal runs on
// the sign-in page).
export const APPROVALS_NOTICE_DISMISS_KEY = "bd:approvalsNoticeDismissed";

let endpointMissing = false;

function sortNewestFirst(rows) {
  return [...rows].sort((a, b) => {
    const d = (Date.parse(b?.ts) || 0) - (Date.parse(a?.ts) || 0);
    return d || (Number(b?.id) || 0) - (Number(a?.id) || 0);
  });
}

/** Pending, de-duplicated approval rows, newest first. */
export function pickPendingApprovals(rows = []) {
  const list = Array.isArray(rows) ? rows : [];
  return dedupeVisibleApprovalLogs(sortNewestFirst(list)).filter(isActionableApproval);
}

async function getJson(url, signal) {
  const r = await fetch(url, { credentials: "include", cache: "no-store", signal });
  if (r.status === 404 || r.status === 405) {
    const err = new Error("not_found");
    err.notFound = true;
    throw err;
  }
  if (!r.ok) throw new Error(`pending_approvals_${r.status}`);
  return r.json();
}

/**
 * @returns {Promise<{ count: number, approvals: object[], complete: boolean }>}
 */
export async function fetchPendingApprovals({ signal } = {}) {
  if (!endpointMissing) {
    try {
      const data = await getJson(`${API}${PENDING_APPROVALS_PATH}`, signal);
      const approvals = pickPendingApprovals(data?.approvals || data?.logs || []);
      return { count: approvals.length, approvals, complete: true };
    } catch (e) {
      if (!e?.notFound) throw e;
      endpointMissing = true;
    }
  }
  const data = await getJson(`${API}${RECENT_LOGS_PATH}`, signal);
  const approvals = pickPendingApprovals(data?.logs || []);
  const totalPages = Number(data?.pagination?.totalPages) || 1;
  return { count: approvals.length, approvals, complete: totalPages <= 1 };
}

const TITLE_MAX = 48;

function clip(text) {
  const t = String(text || "").trim();
  return t.length > TITLE_MAX ? `${t.slice(0, TITLE_MAX - 1).trimEnd()}…` : t;
}

function quoted(text) {
  const t = clip(text);
  return t ? `"${t}"` : "";
}

/** A short name for one queued action: "Escalation to Dana Whitfield". */
export function approvalHeadline(log, nameFor = (v) => v) {
  const meta = logMeta(log);
  const action = meta?.action && typeof meta.action === "object" ? meta.action : {};
  const type = String(meta.actionType || action.type || "").toLowerCase();
  const title = meta.title || action.title || action.topic || "";
  const person = formatPeople(
    action.toEmail || action.to || action.ownerEmail || meta.toEmail || meta.target,
    nameFor,
    1,
  );
  switch (type) {
    case "custom_automation_run":
      return title ? quoted(title) : "A custom automation run";
    case "send_message":
      return title ? `Message ${quoted(title)}` : "A message";
    case "create_project":
      return title ? `Project ${quoted(title)}` : "A new project";
    case "create_task":
      return title ? `Task ${quoted(title)}` : "A new task";
    case "update_task":
      return title ? `An update to ${quoted(title)}` : "A task update";
    case "send_escalation":
      return person ? `Escalation to ${person}` : "An escalation";
    case "send_ping":
      return person ? `Ping to ${person}` : "A ping";
    case "ask_clarifying":
      return person ? `Question for ${person}` : "A question";
    case "send_weekly_report":
      return "The executive report";
    case "schedule_meeting":
      return title ? `Meeting ${quoted(title)}` : "A meeting";
    case "reply_email":
      return person ? `Reply to ${person}` : "An email reply";
    case "send_meeting_notes_external":
      return "Meeting notes for external guests";
    default:
      return title ? quoted(title) : "One action";
  }
}

/** The notice's sentence: one approval by name, more than one by count. */
export function approvalsNoticeText(approvals = [], count = 0, nameFor) {
  const n = Math.max(Number(count) || 0, approvals.length);
  if (n === 1 && approvals[0]) {
    return `${approvalHeadline(approvals[0], nameFor)} is waiting for your approval`;
  }
  if (n === 1) return "1 approval is waiting for you";
  return `${n} approvals are waiting for you`;
}

export function isApprovalsNoticeDismissed() {
  try {
    return sessionStorage.getItem(APPROVALS_NOTICE_DISMISS_KEY) === "1";
  } catch {
    return false;
  }
}

export function dismissApprovalsNotice() {
  try {
    sessionStorage.setItem(APPROVALS_NOTICE_DISMISS_KEY, "1");
  } catch {}
}

export function resetApprovalsNoticeDismissal() {
  try {
    sessionStorage.removeItem(APPROVALS_NOTICE_DISMISS_KEY);
  } catch {}
}
